import { Head } from "vite-react-ssg";
import { useLocation } from "react-router-dom";
import { jsonLdText } from "@/lib/product-seo";
import { brand } from "@kanata/shared";

export const SITE_URL = "https://kanataindonesia.com";

/** The shared preview image, public/og.jpg, and its size. */
const DEFAULT_IMAGE = "/og.jpg";
const DEFAULT_IMAGE_SIZE = { width: 1024, height: 538 };

interface SeoProps {
  title: string;
  description: string;
  /**
   * Path portion of the canonical URL, e.g. "/about". Defaults to the page's
   * own path. It used to default to "/", so every page that left it out
   * (katalog, kontak, the policies) told Google it was a copy of the home
   * page and asked to be dropped from the index in its favour.
   */
  path?: string;
  /** Absolute URL or site-relative path (defaults to the shared OG image). */
  image?: string;
  /** The image's pixel size. WhatsApp and Facebook draw the card on first share only when they know it. */
  imageWidth?: number;
  imageHeight?: number;
  imageAlt?: string;
  /** "product" for a product page, "article" for a blog post; everything else is a website. */
  type?: "website" | "product" | "article";
  jsonLd?: object | object[];
  /** Other formats of this page, e.g. a blog post's Markdown: { type: "text/markdown", path }. */
  alternates?: { type: string; path: string }[];
  /**
   * Keep this page out of search results.
   *
   * For pages that carry a live order in their URL. There is nothing here worth
   * indexing and a real cost to it being indexed — a payment page's address
   * contains an order number and the token that opens it.
   */
  noindex?: boolean;
}

/** Per-page SEO head: title, description, canonical, Open Graph, Twitter, optional JSON-LD. */
export function Seo({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  imageWidth,
  imageHeight,
  imageAlt,
  type = "website",
  jsonLd,
  alternates,
  noindex,
}: SeoProps) {
  const { pathname } = useLocation();
  const url = SITE_URL + (path ?? pathname);
  const img = image.startsWith("http") ? image : SITE_URL + image;
  const size =
    imageWidth && imageHeight
      ? { width: imageWidth, height: imageHeight }
      : image === DEFAULT_IMAGE
        ? DEFAULT_IMAGE_SIZE
        : undefined;
  return (
    <Head>
      <html lang="id" />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <link rel="canonical" href={url} />
      )}
      {alternates?.map((a) => (
        <link key={a.path} rel="alternate" type={a.type} href={SITE_URL + a.path} />
      ))}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={brand.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      {size && <meta property="og:image:width" content={String(size.width)} />}
      {size && <meta property="og:image:height" content={String(size.height)} />}
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:locale" content="id_ID" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {(Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []).map((ld, i) => (
        <script key={i} type="application/ld+json">
          {jsonLdText(ld)}
        </script>
      ))}
    </Head>
  );
}
